import { useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { AIIcon } from 'components/Copilot/AIIcon';
import { cancelIcon, globalStackTokensGapSmall } from 'globalStyles';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getAIAssistantName } from '../aiAssistantName';

interface ICopilotAgentHeaderProps {
  // What the assistant does in this host, one line: the name is the same everywhere, the subtitle is not. Absent:
  // the header shows the name only (the task assistant pane).
  subtitle?: string;
  // When set, the header carries a close button (a pane the user opened can be closed again).
  onClose?: () => void;
}

/**
 * The header of an embedded agent session: the assistant's icon (the one on the navigation bar), its
 * name (the tenant's custom name or the localized default) and, when the host gives one, a one-line subtitle. Shared by
 * the task assistant pane and the document editor, so the assistant looks the same wherever it sits.
 */
export const CopilotAgentHeader = (props: ICopilotAgentHeaderProps): JSX.Element => {
  const { t } = useTranslation(['copilot', 'translation']);
  const appContext = useContext(AppContext);
  const theme = useTheme();

  return (
    <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall} styles={{ root: { minWidth: 0 } }}>
      <IPStack.Item>
        <AIIcon size={28} />
      </IPStack.Item>
      <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
        <IPText block nowrap variant="large" styles={{ root: { fontWeight: 600 } }}>
          {getAIAssistantName(appContext, t)}
        </IPText>
        {props.subtitle && (
          <IPText block variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
            {props.subtitle}
          </IPText>
        )}
      </IPStack.Item>
      {props.onClose && (
        <IPStack.Item>
          <IPIconButton iconProps={cancelIcon} title={t('translation:General.Button.Close')} onClick={props.onClose} />
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default CopilotAgentHeader;
